import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CAST } from "@/data/characters";
import { ISSUES } from "@/data/site";
import { CTA } from "@/components/CTA";
import { Reveal } from "@/components/Reveal";
import { Kicker } from "@/components/Section";
import { Heart } from "@/components/Doodles";

export default function TalentProfile() {
  const { slug } = useParams();
  const c = CAST.find((x) => x.slug === slug);
  const credits = ISSUES.filter((i) => i.crew.some((m) => m.slug === slug));

  if (!c) {
    return (
      <div className="mx-auto max-w-[1400px] bg-onyx px-4 py-32 text-center text-cream sm:px-6">
        <h1 className="font-display text-5xl uppercase text-metallic">The Village is forming.</h1>
        <p className="mt-4 font-body text-silver/70">This profile isn't in the universe yet.</p>
        <CTA to="/talent" variant="pink" className="mt-8">Back to the Cast</CTA>
      </div>
    );
  }

  return (
    <section className="grain-dark relative overflow-hidden border-b border-gold/20 bg-onyx py-16 text-cream sm:py-24">
      <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.03]" />
      <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
        <Link to="/talent" className="inline-flex items-center gap-2 font-head text-sm font-bold uppercase tracking-[0.15em] text-goldlight transition-colors hover:text-gold" data-testid="back-to-talent">
          <ArrowLeft size={16} /> Back to the Cast
        </Link>

        <div className="mt-8 grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <div className="group panel-mono relative gold-frame overflow-hidden">
              <img src={c.img} alt={c.name} className="h-[520px] w-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-onyx/70 to-transparent" />
              <span className="absolute bottom-3 left-3 -rotate-2 border border-gold/50 bg-onyx/85 px-2 py-0.5 font-marker text-lg text-goldlight">{c.tag}</span>
              <span className="absolute right-3 top-3 border border-gold/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.25em] text-gold">{c.code}</span>
            </div>
          </Reveal>

          <div>
            <Kicker>{c.code} · The BLV Universe</Kicker>
            <h1 className="mt-5 font-display text-6xl uppercase leading-[0.85] tracking-tight text-metallic sm:text-8xl" data-testid="cast-name">{c.name}</h1>
            <span className="mt-4 inline-block border border-gold/50 bg-charcoal px-3 py-1 font-head text-sm font-extrabold uppercase tracking-wide text-goldlight">{c.lane}</span>
            <p className="mt-6 max-w-lg font-body text-lg leading-relaxed text-silver/85">{c.bio}</p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="border border-silver/20 bg-charcoal/70 p-5">
                <h3 className="font-head text-sm font-extrabold uppercase tracking-wide text-cream">Appears In</h3>
                <ul className="mt-2 space-y-1 font-body font-semibold text-silver/85">
                  {credits.length ? credits.map((i) => (
                    <li key={i.slug}>
                      <Link to={`/issues/${i.slug}`} className="transition-colors hover:text-goldlight">Issue {i.number} — {i.title}</Link>
                    </li>
                  )) : <li className="text-silver/50">The first issue is developing. 👀</li>}
                </ul>
              </div>
              <div className="border border-gold/40 bg-onyx p-5">
                <h3 className="font-head text-sm font-extrabold uppercase tracking-wide text-gold">Original Character</h3>
                <p className="mt-2 flex items-start gap-2 font-body text-sm leading-relaxed text-silver/80">
                  <Heart className="mt-0.5 h-5 w-5 shrink-0" />
                  {c.name} is one of 14 original illustrated characters who rep the Village. Real cohort members join the directory as they're onboarded.
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <CTA to="/apply" variant="pink" data-testid="cast-apply-cta">This could be you — Apply</CTA>
              <span className="font-marker text-lg text-goldlight/80">BLV AVATAR — COMING SOON ✦</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
